/**
 * @fileoverview App Context
 * @description Global application context for shared state
 * @module context/AppContext
 */

import React, { createContext, useContext, useReducer, useMemo } from 'react';
import { appReducer, initialAppState, appActions } from '@/store';
import { appInsights } from '@/telemetry';

/**
 * App Context
 */
const AppContext = createContext(undefined);

/**
 * App Context Provider
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Context provider
 */
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialAppState);

  const actions = useMemo(
    () => ({
      setTheme: (theme) => dispatch(appActions.setTheme(theme)),
      toggleSidebar: () => dispatch(appActions.toggleSidebar()),
      setLoading: (isLoading) => dispatch(appActions.setLoading(isLoading)),
      addNotification: (notification) => dispatch(appActions.addNotification(notification)),
      removeNotification: (id) => dispatch(appActions.removeNotification(id)),
    }),
    []
  );

  const value = useMemo(() => ({ state, actions }), [state, actions]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

/**
 * Hook to use app context
 * @returns {Object} Context value with state and actions
 */
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {    
    appInsights.trackException({
      name: 'AppContextError',
      message: 'useAppContext called outside of AppProvider',
    });
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

export default AppContext;
